import { DateTime } from 'luxon';
import history from '../../../shared/history';
import { constants } from '../../../shared/enums/constants';
import { StorageUtil } from '../../../shared/utils/storage';
import {
  COMPANY_FUNDING_DATE_DATA,
  COMPANY_FUNDING_ROUND_DATA,
  INDUSTRY_DATA,
  LATEST_FUNDING_DATE_RANGE_DATA,
} from './filter-data';
import {
  V2Lead,
  LeadFinderSecondaryTab,
  LeadRevealType,
  LocationV2Item,
  LocationV2SuggestionType,
} from '../types';
import { leadFinderBulkOperationLimit } from '../../../shared/utils/user-details';

export const LOCATION_V2_TYPE_PREFIX_SEP = ':';

export const MULTILEVEL_INSTANCE_KEY_SEP = '\u001f';

export const buildScopedChildKey = (
  parentKey: string,
  childKeyOrValue: string,
): string => `${parentKey}${MULTILEVEL_INSTANCE_KEY_SEP}${childKeyOrValue}`;

export function withUniqueHierarchicalChildKeys<
  T extends { key: string; children?: any[] }
>(list: T[] | undefined | null): T[] {
  if (!list?.length) return [] as T[];
  return list.map((parent) => {
    if (!parent?.children?.length) return { ...parent } as T;
    const parentKey = parent.key;
    const children = parent.children.map((child: any) => {
      if (
        typeof child.key === 'string' &&
        child.key.includes(MULTILEVEL_INSTANCE_KEY_SEP)
      ) {
        return { ...child };
      }
      const stable = child.key ?? child.value;
      const instanceKey = buildScopedChildKey(parentKey, stable);
      return {
        ...child,
        key: instanceKey,
        value: child.value ?? child.key,
      };
    });
    return { ...parent, children } as T;
  });
}

export const LOCATION_V2_SUPPORTED_TYPES: LocationV2SuggestionType[] = [
  'City',
  'State',
  'Country',
  'Region',
];

export const LOCATION_V2_FILTER_KEYS = {
  location: {
    legacyInclude: 'location',
    legacyExclude: 'excludeLocation',
    v2Include: 'location_v2',
    v2Exclude: 'excludeLocation_v2',
  },
  company_hq_location: {
    legacyInclude: 'company_hq_location',
    legacyExclude: 'excludeCompany_hq_location',
    v2Include: 'company_hq_location_v2',
    v2Exclude: 'excludeCompany_hq_location_v2',
  },
} as const;

export function isLocationFilterKey(
  key: string,
): key is keyof typeof LOCATION_V2_FILTER_KEYS {
  return key === 'location' || key === 'company_hq_location';
}

const getPairedLocationKeys = (key: string) => {
  const locationKey = Object?.keys(LOCATION_V2_FILTER_KEYS)?.find((baseKey) => {
    const pairedKeys =
      LOCATION_V2_FILTER_KEYS[baseKey as keyof typeof LOCATION_V2_FILTER_KEYS];
    return [
      pairedKeys.legacyInclude,
      pairedKeys.legacyExclude,
      pairedKeys.v2Include,
      pairedKeys.v2Exclude,
    ]?.some((pairedKey) => pairedKey === key);
  }) as keyof typeof LOCATION_V2_FILTER_KEYS | undefined;

  if (!locationKey) return null;

  const pairedKeys = LOCATION_V2_FILTER_KEYS[locationKey];
  return {
    valueKeys: [
      pairedKeys.legacyInclude,
      pairedKeys.legacyExclude,
      pairedKeys.v2Include,
      pairedKeys.v2Exclude,
    ],
    includeKeys: [pairedKeys.legacyInclude, pairedKeys.v2Include],
    exactMatchKeys: [
      `${pairedKeys.legacyInclude}_exactMatch`,
      `${pairedKeys.v2Include}_exactMatch`,
    ],
  };
};

export const deletePairedLocationExactMatchFlagsIfEmpty = (
  params: URLSearchParams,
  key: string,
) => {
  const pairedKeys = getPairedLocationKeys(key);
  if (!pairedKeys) return;

  const hasRemainingLocationValue = pairedKeys.includeKeys?.some((valueKey) =>
    params?.getAll(valueKey)?.some(Boolean),
  );

  if (!hasRemainingLocationValue) {
    pairedKeys.exactMatchKeys?.forEach((exactMatchKey) =>
      params.delete(exactMatchKey),
    );
  }
};

export function encodeLocationV2Entry(
  type: LocationV2SuggestionType,
  value: string,
): string {
  return `${type}${LOCATION_V2_TYPE_PREFIX_SEP}${value}`;
}

export function decodeLocationV2Entry(
  raw: string,
): { type: LocationV2SuggestionType; value: string } | null {
  const idx = raw.indexOf(LOCATION_V2_TYPE_PREFIX_SEP);
  if (idx <= 0) return null;
  const type = raw.slice(0, idx) as LocationV2SuggestionType;
  const value = raw.slice(idx + 1);
  if (!LOCATION_V2_SUPPORTED_TYPES.includes(type) || !value) return null;
  return { type, value };
}

export function classifyLocationValue(
  value: string,
  list: any[],
): { type: 'Region' | 'Country'; matchedKey: string } | null {
  if (!value || !Array.isArray(list)) return null;
  const needle = value.trim().toLowerCase();
  if (!needle) return null;
  // eslint-disable-next-line no-restricted-syntax
  for (const item of list) {
    if (item?.key?.toLowerCase() === needle) {
      return { type: 'Region', matchedKey: item.key };
    }
    if (Array.isArray(item?.children)) {
      const child = item.children.find(
        (c: any) =>
          c?.key?.toLowerCase() === needle ||
          (typeof c?.value === 'string' &&
            c.value.trim().toLowerCase() === needle),
      );
      if (child) {
        return { type: 'Country', matchedKey: child.key };
      }
    }
  }
  return null;
}

export function getRegionChildrenKeys(
  regionKey: string,
  list: any[],
): string[] {
  if (!regionKey || !Array.isArray(list)) return [];
  const region = list.find(
    (item) => item?.key?.toLowerCase() === regionKey.toLowerCase(),
  );
  if (!region || !Array.isArray(region.children)) return [];
  return region.children
    .map((c: any) => {
      const leaf = c?.value || c?.key;
      if (!leaf) return '';
      return buildScopedChildKey(region.key, leaf);
    })
    .filter(Boolean);
}

export function suggestionToLocationItem(row: {
  value: string;
  type: LocationV2SuggestionType;
}): LocationV2Item {
  if (
    row.type === 'Country' &&
    row.value.includes(MULTILEVEL_INSTANCE_KEY_SEP)
  ) {
    const i = row.value.indexOf(MULTILEVEL_INSTANCE_KEY_SEP);
    return {
      region: row.value.slice(0, i),
      country: row.value.slice(i + MULTILEVEL_INSTANCE_KEY_SEP.length),
    };
  }
  const parts = row.value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  switch (row.type) {
    case 'City':
      return { city: parts[0], state: parts[1], country: parts[2] };
    case 'State':
      return { state: parts[0], country: parts[1] };
    case 'Country':
      return { country: parts[0] };
    case 'Region':
      return { region: parts[0] };
    default:
      return {};
  }
}

export function locationItemToSuggestion(
  item: LocationV2Item,
): { type: LocationV2SuggestionType; value: string } | null {
  if (!item) return null;
  if (item.city) {
    return {
      type: 'City',
      value: [item.city, item.state, item.country].filter(Boolean).join(', '),
    };
  }
  if (item.state) {
    return {
      type: 'State',
      value: [item.state, item.country].filter(Boolean).join(', '),
    };
  }
  if (item.country) return { type: 'Country', value: item.country };
  if (item.region) return { type: 'Region', value: item.region };
  return null;
}

export function formatLocationV2ItemForDisplay(
  item: LocationV2Item | string | null | undefined,
): string {
  if (!item) return '';
  if (typeof item === 'string') return item;
  const ordered = [item.city, item.state];
  if (item.region && item.country) {
    ordered.push(item.region, item.country);
  } else {
    ordered.push(item.country, item.region);
  }
  return ordered.filter((p) => !!p && String(p).trim() !== '').join(', ');
}

export const validateNumberForQueryParams = (
  value: string | null,
  fallbackValue = 1,
) =>
  value === null || Number.isNaN(Number(value)) || Number(value) < 1
    ? fallbackValue
    : Number(value);

/** True when value is a positive integer company id (0 and non-integers are not ids). */
export const isPositiveCompanyId = (
  value: string | number | null | undefined,
): boolean => {
  if (value === null || value === undefined || value === '') return false;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isInteger(n) && n > 0;
};

/** Parse filter values to company ids accepted by names-by-ids API. */
export const toPositiveCompanyIds = (
  values: Array<string | number>,
): number[] =>
  values
    .map((v) => (typeof v === 'number' ? v : Number(String(v).trim())))
    .filter((n) => Number.isInteger(n) && n > 0);

export function decodeCreatedDateToRange(
  createdDate: unknown,
): [string, string] | null {
  if (typeof createdDate !== 'string') return null;
  const parts = createdDate
    .split(',')
    .map((p) => decodeURIComponent(String(p).trim()))
    .filter(Boolean);
  if (parts.length !== 2) return null;
  return [parts[0], parts[1]];
}

export function formatCreatedDateRangeForApi(
  startDate: Date,
  endDate: Date,
  options?: { encode?: boolean },
): string {
  const encode = options?.encode ?? false;

  const startIso = DateTime.fromJSDate(startDate)
    .startOf('day')
    .toISO({ includeOffset: true, suppressMilliseconds: false });
  const endIso = DateTime.fromJSDate(endDate)
    .endOf('day')
    .toISO({ includeOffset: true, suppressMilliseconds: false });

  // Luxon types allow null, but with valid Date input this should be a string.
  const safeStart = startIso ?? '';
  const safeEnd = endIso ?? '';

  return encode
    ? `${encodeURIComponent(safeStart)},${encodeURIComponent(safeEnd)}`
    : `${safeStart},${safeEnd}`;
}

export const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

export const getExceptFilterValues = (keys: string[], list: any[]) => {
  if (!keys?.length) {
    return [];
  }
  return keys.filter(
    (value) =>
      !list.some(
        (item) =>
          item.key.toLowerCase() === value.toLowerCase() ||
          (item.children &&
            item.children.some(
              (childItem) =>
                childItem.key.toLowerCase() === value.toLowerCase(),
            )),
      ),
  );
};

export const getExceptFilterKeys = (values: string[], list: any[]) => {
  if (!values?.length) {
    return [];
  }
  return values.filter(
    (value) =>
      !list.some(
        (item) =>
          item.value.toLowerCase() === value.toLowerCase() ||
          (item.children &&
            item.children.some(
              (childItem) =>
                childItem.value.toLowerCase() === value.toLowerCase(),
            )),
      ),
  );
};

export const getFilterValues = (keys: string[], list: any[]) => {
  const values: string[] = [];
  if (!keys?.length || !list?.length) return values;

  keys.forEach((key) => {
    list.some((item) => {
      if (item?.key?.toLowerCase() === key?.toLowerCase()) {
        values.push(item?.value || item?.label);
        return true;
      }
      if (item?.children?.length) {
        const child = item.children.find(
          (childItem) => childItem.key.toLowerCase() === key.toLowerCase(),
        );
        if (child) {
          values.push(child.value || child?.label);
          return true;
        }
      }
      return false;
    });
  });

  return values;
};

/**
 * Formats a numeric range string for display with comma-separated thousands.
 * e.g. "1000-5000" -> "1,000-5,000", "10000+" -> "10,000+"
 * Returns original value for non-numeric formats (select, custom, etc.).
 */
export const formatNumericRangeForDisplay = (value: string): string => {
  if (!value || typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (
    trimmed === '' ||
    trimmed === 'select' ||
    trimmed.toLowerCase() === 'custom'
  ) {
    return value;
  }
  if (trimmed.endsWith('+')) {
    const num = parseInt(trimmed.slice(0, -1), 10);
    if (!Number.isNaN(num)) {
      return `${num.toLocaleString('en-US')}+`;
    }
    return value;
  }
  if (trimmed.startsWith('<')) {
    const num = parseInt(trimmed.slice(1), 10);
    if (!Number.isNaN(num)) {
      return `<${num.toLocaleString('en-US')}`;
    }
    return value;
  }
  if (trimmed.includes('-')) {
    const [minStr, maxStr] = trimmed.split('-').map((s) => s.trim());
    const minNum = parseInt(minStr, 10);
    const maxNum = parseInt(maxStr, 10);
    if (!Number.isNaN(minNum) && !Number.isNaN(maxNum)) {
      return `${minNum.toLocaleString('en-US')}-${maxNum.toLocaleString(
        'en-US',
      )}`;
    }
  }
  return value;
};

/**
 * Formats min/max range for display with comma-separated thousands.
 * Used for object-based ranges like { min, max }.
 */
export const formatMinMaxRange = (
  min: number | string | undefined,
  max?: number | string | undefined,
): string => {
  const minNum =
    typeof min === 'number' ? min : parseInt(String(min ?? ''), 10);
  const maxNum =
    typeof max === 'number' ? max : parseInt(String(max ?? ''), 10);
  const minValid = !Number.isNaN(minNum);
  const maxValid = !Number.isNaN(maxNum);
  if (minValid && maxValid) {
    return `${minNum.toLocaleString('en-US')}-${maxNum.toLocaleString(
      'en-US',
    )}`;
  }
  if (minValid && !maxValid) {
    return `${minNum.toLocaleString('en-US')}+`;
  }
  if (!minValid && maxValid) {
    return `<${maxNum.toLocaleString('en-US')}`;
  }
  return '';
};

export const getFilterValuesWithExtraValues = (
  keys: string[],
  list: any[],
  locationData?: boolean,
) => {
  const values = getFilterValues(keys, list);
  const extraValues = getExceptFilterValues(keys, list);
  const parentKeys = keys?.filter((key) => key?.length === 2);

  const queryParam = new URLSearchParams(history?.location?.search);
  const locationFilterKey =
    queryParam.get('location') || queryParam.get('excludeLocation');

  const exactMatch = (locationFilterKey?.match(/"/g) || [])?.length === 2;

  let data = [...values, ...extraValues];

  if (exactMatch && locationData) {
    const result: string[] = [];
    keys?.forEach((key) => {
      if (parentKeys?.length) {
        parentKeys.forEach((parentKey) => {
          if (!key?.substring(0, 2).includes(parentKey)) {
            if (!result.includes(key)) {
              result.push(key);
            }
          }
        });
      } else if (!result.includes(key)) {
        result.push(key);
      }
    });
    const resultValues = getFilterValues(result, list);

    data = data.map((value) =>
      resultValues.includes(value) ? `"${value}"` : value,
    );
  }

  return data;
};

export const getFilterKeys = (values: string[], list: any[]) => {
  const keys: string[] = [];

  values.forEach((value) => {
    list.some((item) => {
      if (item.value.toLowerCase() === value.toLowerCase()) {
        keys.push(item.key);
        return true;
      }
      if (item?.children?.length) {
        const child = item.children.find(
          (childItem) => childItem.value.toLowerCase() === value.toLowerCase(),
        );
        if (child) {
          keys.push(child.key);
          return true;
        }
      }
      return false;
    });
  });

  return keys;
};

export const getFilterKeysWithExtraKeys = (keys: string[], list: any[]) => [
  ...getFilterKeys(keys, list),
  ...getExceptFilterKeys(keys, list),
];

export const findParentKey = (data: any[], targetKey: string) => {
  let parentKey: string | null = null;
  data.some((item) => {
    if (item?.children?.length) {
      const foundChild = item.children.find(
        (child) => child?.key?.toLowerCase() === targetKey?.toLowerCase(),
      );
      if (foundChild) {
        parentKey = item.key;
        return true;
      }
    }
    return false;
  });
  return parentKey;
};

/**
 * Expands parent industry keys to their children values.
 * When a parent key is provided, it returns all children values instead of the parent.
 * For child keys or non-matching keys, it returns the original value.
 */
export const expandIndustryParentToChildren = (
  keys: string[],
  industryData: any[],
): string[] => {
  const expandedValues: string[] = [];
  const data = withUniqueHierarchicalChildKeys(industryData);

  keys.forEach((key) => {
    // Find if this key is a parent key
    const parentItem = data.find(
      (item) => item?.key?.toLowerCase() === key?.toLowerCase(),
    );

    if (parentItem && parentItem.children?.length) {
      // It's a parent key - add all children values instead
      parentItem.children.forEach((child) => {
        const childValue = child.value || child.key;
        if (!expandedValues.includes(childValue)) {
          expandedValues.push(childValue);
        }
      });
    } else {
      // Check if it's a child key or a custom value
      let foundChildValue: string | null = null;

      // Find the child value by searching through all parent items
      data.some((item) => {
        if (item?.children?.length) {
          const child = item.children.find(
            (c) =>
              c?.key === key ||
              (typeof c?.key === 'string' &&
                c.key.toLowerCase() === key?.toLowerCase()),
          );
          if (child) {
            foundChildValue = child.value || child.key;
            return true; // Stop iterating
          }
        }
        return false;
      });

      if (foundChildValue) {
        // It's a child key - add the child value
        if (!expandedValues.includes(foundChildValue)) {
          expandedValues.push(foundChildValue);
        }
      } else if (!expandedValues.includes(key)) {
        // It's a custom/extra value not in the list - add as-is
        expandedValues.push(key);
      }
    }
  });

  return expandedValues;
};

/**
 * Collapses a full set of hierarchical child values/keys back to the parent key
 * for URL and UI display (inverse of expandIndustryParentToChildren for saved filters).
 */
export const collapseHierarchicalChildrenToParentKeys = (
  items: string[],
  hierarchicalData: any[],
): string[] => {
  if (!items?.length) return [];

  const data = withUniqueHierarchicalChildKeys(hierarchicalData);
  const normalize = (value: string) =>
    String(value ?? '')
      .toLowerCase()
      .trim();

  const matchesToken = (item: string, token: string) => {
    const normalizedItem = normalize(item);
    const normalizedToken = normalize(token);
    if (!normalizedItem || !normalizedToken) return false;
    if (normalizedItem === normalizedToken) return true;
    if (item.includes(MULTILEVEL_INSTANCE_KEY_SEP)) {
      const suffix = item.slice(
        item.indexOf(MULTILEVEL_INSTANCE_KEY_SEP) +
          MULTILEVEL_INSTANCE_KEY_SEP.length,
      );
      return normalize(suffix) === normalizedToken;
    }
    return false;
  };

  const matchesChild = (item: string, child: any, parentKey: string) => {
    const tokens = [
      child.key,
      child.value,
      buildScopedChildKey(parentKey, child.key ?? child.value),
    ].filter(Boolean) as string[];
    return tokens.some((token) => matchesToken(item, token));
  };

  const remaining = [...items];
  const collapsedParents: string[] = [];

  data.forEach((parent) => {
    if (!parent.children?.length) return;

    const parentKey = parent.key;
    const hasParentKey = remaining.some(
      (item) => normalize(item) === normalize(parentKey),
    );

    const allChildrenSelected =
      hasParentKey ||
      parent.children.every((child) =>
        remaining.some((item) => matchesChild(item, child, parentKey)),
      );

    if (!allChildrenSelected) return;

    collapsedParents.push(parentKey);
    const nextRemaining = remaining.filter(
      (item) =>
        normalize(item) !== normalize(parentKey) &&
        !parent.children.some((child) => matchesChild(item, child, parentKey)),
    );
    remaining.length = 0;
    remaining.push(...nextRemaining);
  });

  const leftoverKeys = getFilterKeys(remaining, data);
  const leftoverExtras = getExceptFilterKeys(remaining, data);

  return Array.from(
    new Set([...collapsedParents, ...leftoverKeys, ...leftoverExtras]),
  );
};

export const getV2SavedTabPageSize = () => {
  try {
    const savedPageSize = StorageUtil.getItem('v2LeadFinderSavedTabPageSize');
    return savedPageSize
      ? Number(savedPageSize)
      : constants.LEAD_FINDER_DEFAULT_PAGE_SIZE;
  } catch (error) {
    return constants.LEAD_FINDER_DEFAULT_PAGE_SIZE;
  }
};

export const getTimeAgoString = (timestamp) => {
  const timestampDate: any = new Date(timestamp);
  const currentDate: any = new Date();
  const timeDifference = currentDate - timestampDate;
  const seconds = Math.floor(timeDifference / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) {
    return 'Just Now';
  }
  if (minutes < 60) {
    return `${minutes} minutes ago`;
  }
  if (hours < 24) {
    return `${hours} hours ago`;
  }
  return `${days} days ago`;
};

export const capitalizeFirstLetter = (string = '') => {
  if (string) {
    return string?.charAt(0).toUpperCase() + string?.slice(1);
  }
  return '';
};

type DatePresetOption = { key: string; label: string };

const toDateOnly = (dateStr: string): string => {
  const trimmed = (dateStr || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const dt = DateTime.fromISO(trimmed);
  return (dt.isValid ? dt.toISODate() : null) ?? trimmed;
};

const dateRangesMatch = (rangeA: string, rangeB: string): boolean => {
  if (!rangeA?.includes(' - ') || !rangeB?.includes(' - ')) return false;
  const [aStart, aEnd] = rangeA.split(' - ').map((p) => toDateOnly(p.trim()));
  const [bStart, bEnd] = rangeB.split(' - ').map((p) => toDateOnly(p.trim()));
  return (
    (aStart === bStart && aEnd === bEnd) || (aStart === bEnd && aEnd === bStart)
  );
};

/**
 * Reverse-maps a parsed date range back to the matching semantic preset in a list
 * (e.g. last_30_days / last_90_days / last_6_months / last_year).
 * Returns null when no preset matches within a 2-day tolerance.
 */
export const matchSemanticDatePreset = <T extends { key: string }>(
  filterMin: Date,
  filterMax: Date,
  presetList: T[],
): T | null => {
  const today = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const TOLERANCE_DAYS = 2;
  const daysDiff = (a: Date, b: Date) =>
    Math.abs(Math.floor(a.getTime() / dayMs) - Math.floor(b.getTime() / dayMs));

  const last30 = new Date(today);
  last30.setDate(last30.getDate() - 30);
  const last90 = new Date(today);
  last90.setDate(last90.getDate() - 90);
  const last6m = new Date(today);
  last6m.setMonth(last6m.getMonth() - 6);
  const lastYear = new Date(today);
  lastYear.setFullYear(lastYear.getFullYear() - 1);

  const expectedMinByKey: Record<string, Date> = {
    last_30_days: last30,
    last_90_days: last90,
    last_6_months: last6m,
    last_year: lastYear,
  };

  return (
    presetList.find((item) => {
      if (['custom', 'all time', 'select'].includes(item.key)) return false;
      const expectedMin = expectedMinByKey[item.key];
      if (!expectedMin) return false;
      return (
        daysDiff(filterMin, expectedMin) <= TOLERANCE_DAYS &&
        daysDiff(filterMax, today) <= TOLERANCE_DAYS
      );
    }) ?? null
  );
};

export const getFundingDatePresetLabel = (
  urlValue: string,
  presetList: DatePresetOption[],
): string | null => {
  const trimmed = (urlValue || '').trim();
  if (!trimmed || trimmed === 'custom') return null;
  if (trimmed === 'all time') return 'All Time';

  const [exactLabel] = getFilterValues([trimmed], presetList);
  if (exactLabel) return exactLabel;

  if (!trimmed.includes(' - ')) return null;

  const rangeLabel =
    presetList.find(
      (item) =>
        item.key.includes(' - ') &&
        item.key !== 'custom' &&
        dateRangesMatch(item.key, trimmed),
    )?.label ?? null;
  if (rangeLabel) return rangeLabel;

  const [startStr, endStr] = trimmed.split(' - ').map((p) => p.trim());
  const d1 = new Date(toDateOnly(startStr));
  const d2 = new Date(toDateOnly(endStr));
  if (!Number.isNaN(d1.getTime()) && !Number.isNaN(d2.getTime())) {
    const [earlier, later] = d1 <= d2 ? [d1, d2] : [d2, d1];
    const matched = matchSemanticDatePreset(earlier, later, presetList);
    if (matched) return matched.label;

    const dt1 = DateTime.fromISO(toDateOnly(startStr));
    const dt2 = DateTime.fromISO(toDateOnly(endStr));
    if (dt1.isValid && dt2.isValid) {
      const storedSpan = Math.round(Math.abs(dt2.diff(dt1, 'days').days));
      const spanMatch = presetList.find((item) => {
        if (!item.key.includes(' - ') || item.key === 'custom') return false;
        const [kStart, kEnd] = item.key
          .split(' - ')
          .map((p) => toDateOnly(p.trim()));
        const kDt1 = DateTime.fromISO(kStart);
        const kDt2 = DateTime.fromISO(kEnd);
        if (!kDt1.isValid || !kDt2.isValid) return false;
        const presetSpan = Math.round(Math.abs(kDt2.diff(kDt1, 'days').days));
        return Math.abs(presetSpan - storedSpan) <= 2;
      });
      if (spanMatch) return spanMatch.label;
    }
  }

  return null;
};

const applyFundingDateDisplayLabel = (
  displayPayload: Record<string, unknown>,
  payloadKey: string,
  urlValue: string | null | undefined,
  presetList: DatePresetOption[],
): void => {
  let label = urlValue ? getFundingDatePresetLabel(urlValue, presetList) : null;

  if (!label) {
    const apiValue = displayPayload[payloadKey];
    if (apiValue && typeof apiValue === 'object') {
      const dateValue = apiValue as {
        min?: string;
        max?: string;
        allTime?: number;
        exists?: boolean;
      };
      if (dateValue.allTime === 1 || dateValue.exists === true) {
        label = 'All Time';
      } else if (dateValue.min && dateValue.max) {
        label = getFundingDatePresetLabel(
          `${dateValue.min} - ${dateValue.max}`,
          presetList,
        );
      }
    }
  }

  if (label) displayPayload[payloadKey] = label;
};

const applyRecentSearchDateLabels = (
  displayPayload: Record<string, unknown>,
  params: URLSearchParams,
  isCompany: boolean,
): void => {
  if (isCompany) {
    applyFundingDateDisplayLabel(
      displayPayload,
      'company_funding_date',
      params.get('funding_rounds_announced_date'),
      COMPANY_FUNDING_DATE_DATA,
    );
    applyFundingDateDisplayLabel(
      displayPayload,
      'last_funding_round_announced_date',
      params.get('last_funding_round_announced_date'),
      COMPANY_FUNDING_DATE_DATA,
    );
  } else {
    applyFundingDateDisplayLabel(
      displayPayload,
      'company_funding_date',
      params.get('company_funding_date'),
      LATEST_FUNDING_DATE_RANGE_DATA,
    );
  }
};

/** Build display payload when saving a recent search. */
export const buildRecentSearchDisplayPayload = (
  apiPayload: Record<string, unknown>,
  params: URLSearchParams,
  isCompany: boolean,
): Record<string, unknown> => {
  const displayPayload = { ...apiPayload };
  applyRecentSearchDateLabels(displayPayload, params, isCompany);
  return displayPayload;
};

/** Resolve labels for stored recent searches (including entries saved before displayPayload). */
export const resolveRecentSearchDisplayPayload = (entry: {
  url?: string;
  payload?: Record<string, unknown>;
  displayPayload?: Record<string, unknown>;
}): Record<string, unknown> => {
  if (entry.displayPayload) return { ...entry.displayPayload };

  const base = { ...(entry.payload || {}) } as Record<string, unknown>;
  const queryIndex = (entry.url || '').indexOf('?');
  if (queryIndex === -1) return base;

  const urlStr = entry.url || '';
  const params = new URLSearchParams(urlStr.slice(queryIndex + 1));
  const isCompany = urlStr.includes('/v2/leads/company/');
  return buildRecentSearchDisplayPayload(base, params, isCompany);
};

export const mapObjectToString = (
  obj,
  companyListsData,
  peopleListsData,
  companyIdNameMap?: Record<string, string>,
) => {
  let mappedString = '';

  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const key in obj) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(key)) {
      let formattedValue;
      const value = obj[key];

      if (Array.isArray(value)) {
        // Handle arrays
        if (key === 'visits_breakdown_by_gender') {
          const pct = value[0].percentage;
          const rangeStr = formatMinMaxRange(pct?.min, pct?.max);
          const isPercentage =
            pct?.min != null &&
            pct?.max != null &&
            Number(pct.min) <= 100 &&
            Number(pct.max) <= 100;
          formattedValue = rangeStr
            ? `${value[0].gender}: ${rangeStr}${isPercentage ? '%' : ''}`
            : `${value[0].gender}: ${value[0].percentage?.min}-${value[0].percentage?.max}%`;
        } else if (key === 'visits_breakdown_by_country') {
          formattedValue =
            value?.length > 1
              ? `${value[0].country}: ${value[0].percentage?.min ?? 0}-${
                  value[0].percentage?.max ?? 0
                }%+${value.length - 1}`
              : `${value[0].country}: ${value[0].percentage?.min ?? 0}-${
                  value[0].percentage?.max ?? 0
                }%`;
        } else if (
          key === 'employee_count_seniority' ||
          key === 'employee_count_department'
        ) {
          formattedValue =
            value?.length > 1
              ? `${value[0].group}: ${value[0].count?.min ?? 0}-${
                  value[0].count?.max ?? 0
                }+${value.length - 1}`
              : `${value[0].group}: ${value[0].count?.min ?? 0}-${
                  value[0].count?.max ?? 0
                }`;
        } else if (key === 'base_salary') {
          formattedValue =
            value?.length > 1
              ? `${value[0]?.title?.includes?.[0] ?? ''}: ${
                  value[0].range?.min ?? 0
                }-${value[0].range?.max ?? 0}+${value.length - 1}`
              : `${value[0]?.title?.includes?.[0] ?? ''}: ${
                  value[0].range?.min ?? 0
                }-${value[0].range?.max ?? 0}`;
        } else if (key === 'look_alike_company_name') {
          formattedValue =
            value?.length > 1
              ? `${value[0]?.value ?? ''}+${value.length - 1}`
              : `${value[0]?.value ?? ''}`;
        } else {
          formattedValue =
            value?.length > 1 ? `${value[0]}+${value.length - 1}` : value[0];
        }
      } else if (typeof value === 'object' && value !== null) {
        // Handle nested objects (new filter format)
        const parts = [];

        if (value.includes && value.includes.length > 0) {
          let firstValue = value.includes[0];
          if (key === 'location_v2' || key === 'company_hq_location_v2') {
            firstValue = formatLocationV2ItemForDisplay(firstValue);
          }
          if (value.exactMatch === 1) {
            firstValue = `"${firstValue}"`;
          }
          if (key === 'companyList') {
            const companyListName = companyListsData?.find(
              (list) => list.id === firstValue,
            )?.label;
            if (companyListName) {
              firstValue = companyListName;
            }
          }
          if (key === 'peopleList') {
            const peopleListName = peopleListsData?.find(
              (list) => list.id === firstValue,
            )?.label;
            if (peopleListName) {
              firstValue = peopleListName;
            }
          }
          if (key === 'company_ids' && companyIdNameMap) {
            const resolved = companyIdNameMap[String(firstValue)];
            if (resolved != null) {
              firstValue = resolved;
            }
          }

          const includeStr =
            value.includes.length > 1
              ? `${firstValue}+${value.includes.length - 1}`
              : firstValue;

          parts.push(includeStr);
        }
        if (value.title?.includes && value.title?.includes.length > 0) {
          let firstValue = value.title?.includes[0];
          if (value.exactMatch === 1) {
            firstValue = `"${firstValue}"`;
          }
          const includeStr =
            value.title?.includes.length > 1
              ? `${firstValue}+${value.includes.length - 1}`
              : firstValue;

          parts.push(includeStr);
        }
        if (value.title?.excludes && value.title?.excludes.length > 0) {
          const excludeStr =
            value.title?.excludes.length > 1
              ? `exclude ${value.title?.excludes[0]}+${
                  value.title?.excludes.length - 1
                }`
              : `exclude ${value.title?.excludes[0]}`;
          parts.push(excludeStr);
        }

        if (value.excludes && value.excludes.length > 0) {
          let firstValue = value.excludes[0];
          if (key === 'location_v2' || key === 'company_hq_location_v2') {
            firstValue = formatLocationV2ItemForDisplay(firstValue);
          }
          if (value.exactMatch === 1) {
            firstValue = `"${firstValue}"`;
          }
          if (key === 'companyList') {
            const companyListName = companyListsData?.find(
              (list) => list.id === firstValue,
            )?.label;
            if (companyListName) {
              firstValue = companyListName;
            }
          }
          if (key === 'peopleList') {
            const peopleListName = peopleListsData?.find(
              (list) => list.id === firstValue,
            )?.label;
            if (peopleListName) {
              firstValue = peopleListName;
            }
          }
          if (key === 'company_ids' && companyIdNameMap) {
            const resolved = companyIdNameMap[String(firstValue)];
            if (resolved != null) {
              firstValue = resolved;
            }
          }
          const excludeStr =
            value.excludes.length > 1
              ? `exclude ${firstValue}+${value.excludes.length - 1}`
              : `exclude ${firstValue}`;
          parts.push(excludeStr);
        }

        if (value.shouldSearchPast === 1 || value.shouldSearchPast === true) {
          parts.push('Include Past companies');
        }

        if (value.min && value.max) {
          parts.push(`${value.min}-${value.max}`);
        }
        if (value.min && !value.max) {
          parts.push(`${value.min}+`);
        }
        if (!value.min && value.max) {
          parts.push(`<${value.max}`);
        }
        if (value.isUnknown !== undefined) {
          if (value.isUnknown === 1) {
            parts.push('isUnknown');
          } else {
            parts.push('isKnown');
          }
        }
        if (value.exists === true) {
          parts.push('all time');
        }

        if (value.duration && value.direction && value.points_range) {
          parts.push(
            `${value.duration}: ${value.direction}: ${
              value.points_range?.min ?? 0
            }-${value.points_range?.max ?? 0}`,
          );
        }

        formattedValue = parts.join(', ') || value.toString();
      } else if (
        key === 'decision_maker' ||
        key === 'is_b2b' ||
        key === 'company_bootstraped'
      ) {
        if (value === 1) {
          formattedValue = 'Yes';
        } else {
          formattedValue = 'No';
        }
      } else {
        // Handle primitives
        formattedValue = value.toString();
      }

      if (formattedValue) {
        mappedString += `${formattedValue}, `;
      }
    }
  }

  // Remove the trailing comma and space
  mappedString = mappedString.slice(0, -2);

  return mappedString;
};

export const V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY =
  'v2LeadFinderRecentSearches';

export const V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_PEOPLE = V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY;
export const V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY =
  'v2LeadFinderRecentSearchesCompany';

export const LEAD_PROFILE_MODAL_SIZE = 'v2LeadProfileModalSize';
export const LEAD_COMPANY_PROFILE_MODAL_SIZE = 'v2LeadCompanyProfileModalSize';
export const COMPANY_PROFILE_MODAL_SIZE = 'v2CompanyProfileModalSize';

export const LEADS_PER_PAGE = 25;
export const MAX_PAGINATION_TOTAL_ROWS = 10000;

export const MAPPED_KEYS = {
  skills: 'skill',
  school: 'school',
  job_title: 'job-title',
  department: 'department',
  active_job_postings_title: 'job-title',
  base_salary_title: 'job-title',
};

export const V2_MAPPED_KEYS = {
  location: 'location',
  company_hq_location: 'location',
  visits_breakdown_by_country: 'location',
  technologies_used: 'technology',
  job_title: 'job-title',
};

export const LOCAL_SEARCH_MAPPED_KEYS = {
  company_industry: INDUSTRY_DATA,
  funding_rounds_name: COMPANY_FUNDING_ROUND_DATA,
};

export const NOT_ALLOWED_APPEND_CHECKBOXES = ['managementLevels'];

export const TEASER_FIELD_MAPPING: Record<string, boolean> = {
  full_name: true, // Name -> Yes
  job_title: true, // Job Title -> Yes
  company_name: true, // Company Name -> Yes
  emails: false, // Emails -> No
  phones: false, // Phones -> No
  department: true, // Department -> Yes
  social_link: false, // Social URLs -> No
  location_country: true, // Country -> Yes
  location_state: true, // State -> Yes
  location_city: true, // Location City -> Yes
  company_industry: false, // Company Industry -> No
  company_domain: false, // Company Domain -> No (not in interface, but include for completeness)
  company_annual_revenue: false, // Company Revenue -> No
  company_founded_year: false, // Company Founded Year -> No
  total_experience: true, // Total Experience -> Yes
  management_level: false, // Management Level -> No
  decision_maker: true, // Decision Maker -> Yes
  jobs_changed_within: false, // Jobs Changed Within -> No
  skills: false, // Skills -> No
  experience_at_current_company: false, // Current Company experience -> No
  current_role_tenure_months: false, // Tenure In Current Role -> No
  company_is_b2b: true, // Is B2b -> Yes
  company_logo_url: true, // Company Logo URL -> Yes
  company_socials: false, // Company socials -> No
  projected_base_salary_dollars: false, // Projected Base Salary in Dollars -> No
  company_hq_country: false, // Company HQ Location -> No
  company_hq_state: false, // Company HQ Location -> No
  company_hq_city: false, // Company HQ Location -> No
  company_last_funding_round_amount_raised: false, // Funding Amount Range -> No
  company_last_funding_date: false, // Latest Funding Date Range -> No
  institution_name: false, // School -> No
  education_degrees: false, // Education Degrees -> No
  linkedin_url: false, // LinkedIn URL -> (empty, treat as No)
  linkedin_connection_count: false, // LinkedIn Connection -> No
  linkedin_followers_count: false, // LinkedIn Followers -> No
  past_companies: false, // Past Companies -> No
  active_experiences: false, // Active Experiences -> No
  picture_url: true, // Picture URL -> Yes
  company_size_range: false, // Company Size Range -> No
  company_website: false, // Company Website -> No
  company_linkedin: false, // Company LinkedIn -> No
};

// Fields visible to free plan users even when lead is not revealed
export const FREE_PLAN_LEAD_ALLOWED_FIELDS: Set<string> = new Set([
  'full_name',
  'company_name',
  'company_domain',
  'company_logo_url',
  'picture_url',
  'company_website',
]);

// Reveal types that include work email
export const WORK_EMAIL_REVEAL_TYPES = [
  LeadRevealType.Email,
  LeadRevealType.EmailPhone,
  LeadRevealType.EmailPersonalEmail,
  LeadRevealType.EmailPersonalEmailPhone,
];

// Reveal types that include personal email
export const PERSONAL_EMAIL_REVEAL_TYPES = [
  LeadRevealType.PersonalEmail,
  LeadRevealType.PersonalEmailPhone,
  LeadRevealType.EmailPersonalEmail,
  LeadRevealType.EmailPersonalEmailPhone,
];

// Reveal types that include phone
export const PHONE_REVEAL_TYPES = [
  LeadRevealType.Phone,
  LeadRevealType.EmailPhone,
  LeadRevealType.PersonalEmailPhone,
  LeadRevealType.EmailPersonalEmailPhone,
];

/**
 * Checks if a revealed lead has no usable email AND no unrevealed email types left.
 *
 * Only returns true when:
 * - The lead is revealed AND cannot be re-revealed
 * - All revealed email types returned empty data
 * - There are no unrevealed email types remaining (nothing left to try)
 */
export const hasLeadNoEmail = (lead: V2Lead): boolean => {
  if (!lead?.isRevealed || lead?.reReveal !== false) {
    return false;
  }

  const hasWorkEmails = !!lead?.emails && lead.emails.length > 0;
  const hasPersonalEmails =
    !!lead?.personalEmails && lead.personalEmails.length > 0;

  // If lead has any email, it's not "no email"
  if (hasWorkEmails || hasPersonalEmails) {
    return false;
  }

  // Check which email types have been revealed
  const workEmailRevealed =
    !!lead.revealType && WORK_EMAIL_REVEAL_TYPES.includes(lead.revealType);
  const personalEmailRevealed =
    !!lead.revealType && PERSONAL_EMAIL_REVEAL_TYPES.includes(lead.revealType);

  // If at least one email type was revealed and all returned empty → no email
  return workEmailRevealed || personalEmailRevealed;
};

// Helper to check if a lead has valid email (for Save as Prospect / Add to Sequence)
export const hasLeadValidEmail = (lead: V2Lead): boolean =>
  lead?.isRevealed === true &&
  ((!!lead?.emails && lead.emails.length > 0) ||
    (!!lead?.personalEmails && lead.personalEmails.length > 0));

// Helper function to check if a lead should be disabled
export const isLeadDisabled = (
  lead: V2Lead,
  emailRevealingLeads: Record<number, boolean> = {},
  emailPhoneRevealingLeads: Record<number, boolean> = {},
  personalEmailRevealingLeads: Record<number, boolean> = {},
): boolean => {
  const isRevealing =
    lead?.isRevealing === true ||
    lead?.isPersonalEmailRevealing === true ||
    emailRevealingLeads[lead?.id] === true ||
    emailPhoneRevealingLeads[lead?.id] === true ||
    personalEmailRevealingLeads[lead?.id] === true;

  return hasLeadNoEmail(lead) || isRevealing;
};

export const isLeadDisabledForSavedTab = (
  lead: V2Lead,
  emailRevealingLeads: Record<number, boolean> = {},
  emailPhoneRevealingLeads: Record<number, boolean> = {},
  personalEmailRevealingLeads: Record<number, boolean> = {},
): boolean => {
  const isRevealing =
    lead?.isRevealing === true ||
    lead?.isPersonalEmailRevealing === true ||
    emailRevealingLeads[lead?.id] === true ||
    emailPhoneRevealingLeads[lead?.id] === true ||
    personalEmailRevealingLeads[lead?.id] === true;

  // In saved tab, only disable when revealing - allow selecting leads without
  // valid email so they can be added to list (Add to List doesn't require email)
  return isRevealing;
};

export const getRowDisabledTooltip = (
  lead: V2Lead,
  emailRevealingLeads: Record<number, boolean> = {},
  emailPhoneRevealingLeads: Record<number, boolean> = {},
  activeSubTab: LeadFinderSecondaryTab,
  isSelectedRowsLimitReached = false,
  maxSelectedRowsLimit = 0,
  personalEmailRevealingLeads: Record<number, boolean> = {},
): string => {
  // Check if lead is revealing
  const isRevealing =
    lead?.isRevealing === true ||
    lead?.isPersonalEmailRevealing === true ||
    emailRevealingLeads[lead?.id] === true ||
    emailPhoneRevealingLeads[lead?.id] === true ||
    personalEmailRevealingLeads[lead?.id] === true;

  if (isRevealing) {
    return 'Revealing Lead';
  }

  // Check if lead has no email (saved tab: allow selection for Add to List)
  if (activeSubTab !== LeadFinderSecondaryTab.SAVED && hasLeadNoEmail(lead)) {
    return 'Lead does not have any email address';
  }

  // Check if selection limit is reached
  if (isSelectedRowsLimitReached && maxSelectedRowsLimit > 0) {
    return `Maximum ${maxSelectedRowsLimit} leads can be selected`;
  }

  return ''; // No tooltip needed
};

/** Max bulk selection on People → Saved tab */
export const SAVED_TAB_BULK_OPERATION_LIMIT = 25000;

/** Max bulk selection on Company → Saved tab */
export const SAVED_TAB_COMPANY_BULK_OPERATION_LIMIT = 10000;

export function getV2BulkOperationLimit(
  activeSubTab: LeadFinderSecondaryTab,
  options?: { isCompanyTab?: boolean },
): number {
  if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
    return options?.isCompanyTab
      ? SAVED_TAB_COMPANY_BULK_OPERATION_LIMIT
      : SAVED_TAB_BULK_OPERATION_LIMIT;
  }
  return leadFinderBulkOperationLimit();
}

export const INSUFFICIENT_CREDITS_ERROR_MESSAGE = 'Insufficient credits';

export const isInsufficientCreditsError = (message: any) =>
  typeof message === 'string' &&
  message
    .toLowerCase()
    .includes(INSUFFICIENT_CREDITS_ERROR_MESSAGE.toLowerCase());

/**
 * Formats lead finder credits to show up to 2 decimal places, removing only .00
 * @param credits - The number of credits to format
 * @returns Formatted string with credits (e.g., "100" for 100.00, "100.5" for 100.50)
 *
 * @example
 * formatLeadFinderCredits(100); // "100"
 * formatLeadFinderCredits(100.5); // "100.5"
 * formatLeadFinderCredits(100.50); // "100.5"
 * formatLeadFinderCredits(100.00); // "100"
 */
export const formatLeadFinderCredits = (credits: number): string => {
  const truncated = Math.floor(credits * 100) / 100;
  const formatted = truncated.toFixed(2);
  return formatted.replace(/\.00$/, '');
};

// Helper to get min/max range structure
// Helper function to convert company_founded_year relative keys to actual year range
export const convertYearKeyToRange = (key: string): string | null => {
  if (!key) return null;

  const currentYear = new Date().getFullYear();
  const trimmedKey = key.trim(); // Remove any spaces like "1 -3"

  // FIRST: Check if it's already an absolute year range (e.g., "2017-2024")
  // This must be checked BEFORE relative range conversion to avoid misinterpreting custom ranges
  if (trimmedKey.includes('-')) {
    const parts = trimmedKey.split('-');
    if (parts.length === 2) {
      const part1 = parseInt(parts[0].trim(), 10);
      const part2 = parseInt(parts[1].trim(), 10);
      // Check if both parts are valid 4-digit years (absolute year range)
      if (
        !Number.isNaN(part1) &&
        !Number.isNaN(part2) &&
        part1 >= 1900 &&
        part1 <= currentYear &&
        part2 >= 1900 &&
        part2 <= currentYear
      ) {
        return trimmedKey; // Already an absolute year range, return as is
      }
    }
  }

  // SECOND: Handle relative year keys (only if not already an absolute range)
  if (trimmedKey.endsWith('+')) {
    // Handle "20+" format
    const yearsPast = parseInt(trimmedKey.replace('+', ''), 10);
    if (!Number.isNaN(yearsPast)) {
      const minYear = currentYear - yearsPast;
      return `${minYear}-${currentYear}`;
    }
  } else if (trimmedKey.includes('-')) {
    // Handle "1-3" or "1 -3" format (relative range)
    const parts = trimmedKey.split('-').map((p) => parseInt(p.trim(), 10));
    if (parts.length === 2 && !parts.some(Number.isNaN)) {
      // Check if it's a relative range (small numbers, not years)
      // If both parts are less than 100, treat as relative range
      if (parts[0] < 100 && parts[1] < 100) {
        // First number (1) represents current year, second (3) represents years in past
        const maxYear = currentYear; // First number = current year
        const minYear = currentYear - parts[1]; // Second number = years in past
        return `${minYear}-${maxYear}`;
      }
    }
  }

  return null; // Invalid format
};

export const isListFilter = (key: string): boolean => {
  if (key === 'company_list' || key === 'people_list') {
    return true;
  }
  return false;
};

/**
 * Append AI search filters to a URL object by serializing common filter shapes:
 * - arrays -> repeated params
 * - { includes: [...] } -> repeated params for key
 * - { excludes: [...] } -> set excludeKey as comma separated
 * - { min, max } -> set as "min-max"
 * - flags (isUnknown, exactMatch, shouldSearchPast) -> separate params
 *
 * Mutates the passed URL's searchParams.
 */
export const appendAiFiltersToUrl = (currentUrl: URL, filtersPayload: any) => {
  const aiSearchFilters = { ...filtersPayload };

  const appendExcludeKey = (key: string) =>
    `exclude${key.charAt(0).toUpperCase()}${key.slice(1)}`;

  const setFlagParam = (key: string, flagKey: string, value: any) => {
    if (value === 1 || value === '1' || value === true) {
      currentUrl.searchParams.set(`${key}_${flagKey}`, '1');
    } else if (value === 0 || value === '0' || value === false) {
      currentUrl.searchParams.set(`${key}_${flagKey}`, '0');
    }
  };

  if (Object.keys(aiSearchFilters)?.length) {
    const filterEntries = Object.entries(aiSearchFilters);
    filterEntries.forEach(([filterKey, filterValue]) => {
      // Remove existing keys for both include and exclude variants
      currentUrl.searchParams.delete(filterKey);
      currentUrl.searchParams.delete(appendExcludeKey(filterKey));
      // Also remove possible flag keys
      currentUrl.searchParams.delete(`${filterKey}_isUnknown`);
      currentUrl.searchParams.delete(`${filterKey}_exactMatch`);
      currentUrl.searchParams.delete(`${filterKey}_shouldSearchPast`);

      if (filterKey && filterValue !== undefined && filterValue !== null) {
        // Arrays -> append multiple values for the same key
        if (Array.isArray(filterValue)) {
          filterValue.forEach((value) => {
            if (value !== undefined && value !== null && value !== '') {
              currentUrl.searchParams.append(filterKey, String(value));
            }
          });
          return;
        }

        // Objects -> handle structured filters (includes/excludes/min/max/flags)
        if (typeof filterValue === 'object') {
          const obj: any = filterValue;

          // includes -> append repeated filterKey
          if (Array.isArray(obj.includes) && obj.includes.length > 0) {
            obj.includes.forEach((val: any) => {
              if (val !== undefined && val !== null && val !== '') {
                currentUrl.searchParams.append(filterKey, String(val));
              }
            });
          }

          // excludes -> append to excludeKey as comma-separated or repeated values
          if (Array.isArray(obj.excludes) && obj.excludes.length > 0) {
            currentUrl.searchParams.set(
              appendExcludeKey(filterKey),
              obj.excludes.map((v: any) => String(v)).join(','),
            );
          }

          // min/max -> set as "min - max"
          if (obj.min !== undefined || obj.max !== undefined) {
            const min =
              obj.min !== undefined && obj.min !== null ? String(obj.min) : '';
            const max =
              obj.max !== undefined && obj.max !== null ? String(obj.max) : '';
            currentUrl.searchParams.set(filterKey, `${min} - ${max}`);
          }

          // isUnknown flag
          if (obj.isUnknown !== undefined) {
            setFlagParam(filterKey, 'isUnknown', obj.isUnknown);
          }

          // exactMatch flag
          if (obj.exactMatch !== undefined) {
            setFlagParam(filterKey, 'exactMatch', obj.exactMatch);
          }

          // shouldSearchPast flag
          if (obj.shouldSearchPast !== undefined) {
            setFlagParam(filterKey, 'shouldSearchPast', obj.shouldSearchPast);
          }

          return;
        }

        // Primitive values -> set directly
        currentUrl.searchParams.set(filterKey, String(filterValue));
      }
    });
  }
};

export const RESTRICTED_PEOPLE_FILTER_IDS = new Set([
  'filter-jobs-changed-within',
  'filter-current-role-tenure',
  'filter-projected-base-salary',
  'filter-is-b2b',
  'filter-company-bootstraped',
  'filter-revenue',
  'filter-funding-amount',
  'filter-latest-funding-date',
]);

export const RESTRICTED_COMPANY_FILTER_IDS = new Set([
  'filter-funding',
  'filter-last-funding-round',
  'filter-employee-count',
  'filter-seniority-wise-employee-count',
  'filter-department-wise-employee-count',
  'filter-active-job-openings',
  'filter-last-month-website-visitors',
  'filter-visitor-by-gender',
  'filter-visitor-by-country',
  // 'filter-signals',
  // 'filter-news',
  'filter-salary-information',
  'filter-employee-reviews-business-outlook',
  'filter-employee-reviews-ceo-approval',
  'filter-employee-reviews-career-opportunities',
  'filter-employee-reviews-recommend',
]);

/**
 * Compute Lead Reveal credits using three-step precedence:
 * 1) If LEAD.REVEAL + LEAD.REVEAL.BUCKET2 > 0 => use that sum
 * 2) Else if leadFinder.isBucket1Frozen === 1 => use only BUCKET2
 * 3) Otherwise => use BUCKET1 + BUCKET2
 *
 * Inputs may be strings or numbers. Missing values treated as 0.
 */
export const computeLeadRevealCredits = (
  leadRevealMainRaw?: string | number,
  leadRevealBucket1Raw?: string | number,
  leadRevealBucket2Raw?: string | number,
  leadFinderMeta?: any,
): { availableCredits: number; totalCredits: number } => {
  const leadRevealMain = Number(leadRevealMainRaw) || 0;
  const bucket1 = Number(leadRevealBucket1Raw ?? 0);
  const bucket2 = Number(leadRevealBucket2Raw ?? 0);
  const isBucket1Frozen =
    String((leadFinderMeta as any)?.isBucket1Frozen) === '1';

  let availableCredits: number;
  if (leadRevealMain) {
    availableCredits = leadRevealMain + bucket2;
  } else if (isBucket1Frozen) {
    availableCredits = bucket2;
  } else {
    availableCredits = bucket1 + bucket2;
  }

  let totalCredits: number;
  if (leadRevealMain) {
    totalCredits = leadRevealMain + bucket2;
  } else {
    totalCredits = bucket1 + bucket2;
  }

  return { availableCredits, totalCredits };
};

// Delay the event
export const delay = () =>
  new Promise<void>((resolve) => setTimeout(() => resolve(), 0));
